import { Type } from '@angular/core';
import {
  property,
  subclass
} from '@arcgis/core/core/accessorSupport/decorators';
import esriLayer from '@arcgis/core/layers/Layer';

/**
 * Accepts both concrete and abstract ArcGIS layer constructors.
 * Necessary because @arcgis/core v5 declares Layer as abstract, but mixins
 * only extend it — they never call new Base() — so the abstract restriction
 * is irrelevant at runtime.
 */
export type AbstractType<T> = abstract new (...args: any[]) => T;

/**
 * Represents a method with a specific context, arguments, and return type.
 * @template Context - The 'this' context type.
 * @template Args - Method argument types.
 * @template Return - Method return type.
 */
export type JepToolsMethod<
  Context,
  Args extends any[] = any[],
  Return = any
> = (this: Context, ...args: Args) => Return;

/**
 * Defines the structure of a layer jepTools.
 * @template TLayer - The layer type the jepTools extends.
 * @template T - Collection of jepTools methods.
 */
export type LayerJepTools<
  TLayer extends esriLayer = esriLayer,
  T extends Record<string, JepToolsMethod<TLayer>> = Record<
    string,
    JepToolsMethod<TLayer>
  >
> = T;

/**
 * Interface for layers with a jepTools.
 * @template T - Type of the jepTools.
 */
export interface JepToolsLayerInterface<T> {
  jepTools: T;
}

/**
 * Adds jepTools functionality to an ArcGIS layer.
 *
 * This mixin enhances a base ArcGIS layer class by adding a "jepTools" property. The jepTools
 * consists of user-defined methods bound to the layer instance.
 *
 * @template TBase - Base layer constructor type.
 * @template TJepTools - JepTools type to mix in.
 * @param Base - The base layer class to extend.
 * @param extension - A set of methods to include in the jepTools.
 * @returns A new class that extends the base layer and includes the jepTools.
 *
 * @example
 * ```typescript
 * import Layer from '@arcgis/core/layers/Layer';
 * import { JepToolsMixin, JepToolsLayerInterface } from './jep-tools.mixin';
 *
 * type CustomJepTools = {
 *   logLayerName: () => void;
 *   toggleVisibility: () => void;
 *   updateOrder: (order: number) => void;
 * };
 *
 * const jepTools = {
 *   logLayerName() {
 *     console.log(this.title);
 *   },
 *   toggleVisibility() {
 *     this.visible = !this.visible;
 *   },
 *   updateOrder(order: number) {
 *     this.order = order;
 *   }
 * };
 *
 * @subclass('CustomLayer')
 * class CustomLayer extends JepToolsMixin(Type<Layer>, jepTools) {
 *   @property()
 *   order: number = 0;
 *
 *   @property()
 *   stylesRegex!: RegExp;
 * }
 *
 * const layer = new CustomLayer({ title: 'Sample Layer', visible: true });
 * layer.jepTools.logLayerName(); // Logs: 'Sample Layer'
 * layer.jepTools.toggleVisibility(); // Toggles visibility
 * layer.jepTools.updateOrder(5); // Updates order to 5
 * ```
 */
export function JepToolsMixin<
  TBase extends Type<esriLayer>,
  TJepTools extends LayerJepTools<InstanceType<TBase>>
>(
  Base: TBase,
  extension: TJepTools
): Type<InstanceType<TBase> & JepToolsLayerInterface<TJepTools>> {
  @subclass('jmkw.layers.JepToolsLayer')
  class JepToolsLayer extends Base {
    /**
     * The jepTools object containing custom methods for the layer.
     */
    @property()
    jepTools!: TJepTools;

    constructor(...args: any[]) {
      super(...args);

      // Initialize the jepTools by binding methods to the instance context
      this.jepTools = Object.entries(extension).reduce(
        (acc, [key, value]) => {
          if (typeof value === 'function') {
            (acc as any)[key] = (value as () => any).bind(
              this as InstanceType<TBase>
            );
          }
          return acc;
        },
        this.jepTools || ({} as TJepTools)
      );
    }
  }

  return JepToolsLayer as unknown as Type<
    InstanceType<TBase> & JepToolsLayerInterface<TJepTools>
  >;
}
